import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { JournalBankAccountDropdown } from "@/components/shared/form/journal-bank-account-dropdown";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountEntityType } from "@/lib/constants/account-types";
import { useGetAllJournalAccounts } from "@/queries/ledger/accounts/get-all";
import { useUpdateJournalAccounts } from "@/queries/ledger/accounts/update";

const formSchema = z.object({
  journalAccountId: z.string().min(1).max(255).nullable(),
  entityId: z.string().min(1).max(255).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  journalAccountId: string | null;
  entityId: string | null;
  onSubmit: (journalAccountId: string) => Promise<void>;
  allowReset?: boolean;
};

export const BankAccountJournalAccountPicker = ({
  journalAccountId,
  entityId,
  onSubmit,
  allowReset = false,
}: Props) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLFormElement>(null);

  const [updateAccount] = useUpdateJournalAccounts();

  const {
    data: journalAccounts,
    loading,
    error,
    refetch,
  } = useGetAllJournalAccounts({
    query: {
      entityType: {
        op: "in",
        value: [AccountEntityType.BANK_ACCOUNT],
      },
    },
    page: 1,
    limit: 100,
    offset: 0,
    global: "",
    sort: "createdAt",
    order: "desc",
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      journalAccountId,
      entityId,
    },
    values: {
      journalAccountId,
      entityId,
    },
  });

  const handleSubmit = async ({ journalAccountId }: FormValues) => {
    if (!journalAccountId) {
      return;
    }

    await onSubmit(journalAccountId);

    const initialJournalAccountId =
      form.formState.defaultValues?.journalAccountId;

    if (
      initialJournalAccountId &&
      journalAccountId !== initialJournalAccountId
    ) {
      await updateAccount({
        journalAccountIds: [initialJournalAccountId],
        entityId: null,
      });
    }

    refetch();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-10 flex-shrink-0" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full rounded-md border border-destructive/50 bg-destructive/10 p-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-xs text-destructive/80">{t("generic_error")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form ref={ref} className="grid gap-2">
        <JournalBankAccountDropdown
          name="journalAccountId"
          options={journalAccounts ?? []}
          fullWidth
          allowReset={allowReset}
          container={ref}
          modal={false}
        />
        <AutoSaveForm onSubmit={handleSubmit} />
      </form>
    </Form>
  );
};
